import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import
{
    CheckedNoteAvailable,
    CheckedVersionAvailable,
    ClearCheckNote,
    ClearCheckVersion,
    ClearCreatedNote,
    ClearCreatedProduct,
    ClearCreatedSubscription,
    ClearCreatedVersion,
    ClearCreateNoteError,
    ClearCreateProductError,
    ClearCreateSubscriptionError,
    ClearCreateUnsubscribeError,
    ClearCreateVersionError,
    ClearEdited,
    ClearExported,
    ClearImportedTranslation,
    ClearNoteDeleted,
    ClearNotesError,
    ClearProductDeleted,
    ClearProductError,
    ClearProductsError,
    ClearRemovedSubscription,
    ClearSendMail,
    ClearUnsubscribed,
    ClearVersionDeleted,
    ClearVersionsError,
    CreateNoteError,
    CreateProductError,
    CreateSubscriptionError,
    CreateUnsubscribeError,
    CreateVersionError,
    ImportedTranslation,
    InitialVersionsLoaded,
    NoteDeleted,
    NotesError,
    ProductDeleted,
    ProductError,
    ProductsError,
    SetCheckNote,
    SetCheckVersion,
    SetCreatedNote,
    SetCreatedProduct,
    SetCreatedSubscription,
    SetCreatedVersion,
    SetEdited,
    SetExported,
    SetProductChanged,
    SetRemovedSubscription,
    SetSendMail,
    SetUnsubscribed,
    VersionDeleted,
    VersionsError,
} from "./product-api-status.actions";
import { CheckStatusEnum, defaultProductApiStatusState, IProductApiStatusState } from "./product-api-status.definitions";

@State<IProductApiStatusState>({
    name: 'productApiStatus',
    defaults: defaultProductApiStatusState,
})
@Injectable()
export class ProductApiStatusState
{
    constructor()
    {
    }

    @Selector([ProductApiStatusState])
    public static checkVersion(state: IProductApiStatusState)
    {
        return state.checkVersion;
    }

    @Selector([ProductApiStatusState])
    public static checkNote(state: IProductApiStatusState)
    {
        return state.checkNote;
    }

    @Selector([ProductApiStatusState])
    public static editError(state: IProductApiStatusState)
    {
        return state.editError;
    }

    @Selector([ProductApiStatusState])
    public static productsError(state: IProductApiStatusState)
    {
        return state.productsError;
    }

    @Selector([ProductApiStatusState])
    public static productError(state: IProductApiStatusState)
    {
        return state.productError;
    }

    @Selector([ProductApiStatusState])
    public static versionsError(state: IProductApiStatusState)
    {
        return state.versionsError;
    }

    @Selector([ProductApiStatusState])
    public static notesError(state: IProductApiStatusState)
    {
        return state.notesError;
    }

    @Selector([ProductApiStatusState])
    public static productDeleted(state: IProductApiStatusState)
    {
        return state.productDeleted;
    }

    @Selector([ProductApiStatusState])
    public static versionDeleted(state: IProductApiStatusState)
    {
        return state.versionDeleted;
    }

    @Selector([ProductApiStatusState])
    public static noteDeleted(state: IProductApiStatusState)
    {
        return state.noteDeleted;
    }

    @Selector([ProductApiStatusState])
    public static createVersionError(state: IProductApiStatusState)
    {
        return state.createVersionError;
    }

    @Selector([ProductApiStatusState])
    public static createProductError(state: IProductApiStatusState)
    {
        return state.createProductError;
    }

    @Selector([ProductApiStatusState])
    public static createNoteError(state: IProductApiStatusState)
    {
        return state.createNoteError;
    }

    @Selector([ProductApiStatusState])
    public static editedValue(state: IProductApiStatusState)
    {
        return state.editedValue;
    }

    @Selector([ProductApiStatusState])
    public static exportedTranslation(state: IProductApiStatusState)
    {
        return state.exportedTranslation;
    }

    @Selector([ProductApiStatusState])
    public static addedVersion(state: IProductApiStatusState)
    {
        return state.addedVersion;
    }

    @Selector([ProductApiStatusState])
    public static addedProduct(state: IProductApiStatusState)
    {
        return state.addedProduct;
    }

    @Selector([ProductApiStatusState])
    public static addedNote(state: IProductApiStatusState)
    {
        return state.addedNote;
    }

    @Selector([ProductApiStatusState])
    public static addedSubscription(state: IProductApiStatusState)
    {
        return state.addedSubscription;
    }

    @Selector([ProductApiStatusState])
    public static removedSubscription(state: IProductApiStatusState)
    {
        return state.removedSubscription;
    }

    @Selector([ProductApiStatusState])
    public static unsubscribed(state: IProductApiStatusState)
    {
        return state.unsubscribed;
    }

    @Selector([ProductApiStatusState])
    public static initialVersionsLoaded(state: IProductApiStatusState)
    {
        return state.initialVersionLoad;
    }

    @Selector([ProductApiStatusState])
    public static sendEmail(state: IProductApiStatusState)
    {
        return state.sendMail;
    }

    @Selector([ProductApiStatusState])
    public static createEmailError(state: IProductApiStatusState)
    {
        return state.createEmailError;
    }

    @Selector([ProductApiStatusState])
    public static importedTranslation(state: IProductApiStatusState)
    {
        return state.importedTranslation;
    }

    @Selector([ProductApiStatusState])
    public static createSubscriptionError(state: IProductApiStatusState)
    {
        return state.createSubscriptionError;
    }

    @Action(SetProductChanged)
    public setProductChanged({ patchState }: StateContext<IProductApiStatusState>, action: SetProductChanged)
    {
        patchState({ changedProduct: action.value })
    }

    @Action(SetEdited)
    public setEdited({ patchState }: StateContext<IProductApiStatusState>, action: SetEdited)
    {
        patchState({ editedValue: action.value })
    }

    @Action(SetExported)
    public setExported({ patchState }: StateContext<IProductApiStatusState>, action: SetExported)
    {
        patchState({ exportedTranslation: action.value })
    }

    @Action(SetCreatedProduct)
    public setCreatedProduct({ patchState }: StateContext<IProductApiStatusState>, action: SetCreatedProduct)
    {
        patchState({ addedProduct: action.value })
    }

    @Action(SetCreatedVersion)
    public setCreatedVersion({ patchState }: StateContext<IProductApiStatusState>, action: SetCreatedVersion)
    {
        patchState({ addedVersion: action.value })
    }

    @Action(SetCreatedNote)
    public setCreatedNote({ patchState }: StateContext<IProductApiStatusState>, action: SetCreatedNote)
    {
        patchState({ addedNote: action.value })
    }

    @Action(SetCreatedSubscription)
    public setCreatedSubscription({ patchState }: StateContext<IProductApiStatusState>, action: SetCreatedSubscription)
    {
        patchState({ addedSubscription: action.value })
    }

    @Action(SetUnsubscribed)
    public setUnsubscribed({ patchState }: StateContext<IProductApiStatusState>, action: SetUnsubscribed)
    {
        patchState({ unsubscribed: action.value })
    }

    @Action(SetRemovedSubscription)
    public setRemovedSubscription({ patchState }: StateContext<IProductApiStatusState>, action: SetRemovedSubscription)
    {
        patchState({ removedSubscription: action.value })
    }

    @Action(SetSendMail)
    public setSendMail({ patchState }: StateContext<IProductApiStatusState>, action: SetSendMail)
    {
        patchState({ sendMail: action.value })
    }

    @Action(ClearSendMail)
    public clearSetSendMail({ patchState }: StateContext<IProductApiStatusState>, action: ClearSendMail)
    {
        patchState({ sendMail: null })
    }

    @Action(SetCheckVersion)
    public setCheckVersion({ patchState }: StateContext<IProductApiStatusState>, action: SetCheckVersion)
    {
        patchState({ checkVersion: action.value })
    }

    @Action(SetCheckNote)
    public setCheckNote({ patchState }: StateContext<IProductApiStatusState>, action: SetCheckNote)
    {
        patchState({ checkNote: action.value })
    }

    @Action(ProductsError)
    public productsError({ patchState }: StateContext<IProductApiStatusState>, action: ProductsError)
    {
        patchState({ productsError: action.error })
    }

    @Action(ClearProductsError)
    public clearProductsError({ patchState }: StateContext<IProductApiStatusState>, action: ClearProductsError)
    {
        patchState({ productsError: null })
    }

    @Action(ProductError)
    public productError({ patchState }: StateContext<IProductApiStatusState>, action: ProductError)
    {
        patchState({ productError: action.error })
    }

    @Action(ClearProductError)
    public clearProductError({ patchState }: StateContext<IProductApiStatusState>, action: ClearProductError)
    {
        patchState({ productError: null })
    }

    @Action(VersionsError)
    public versionError({ patchState }: StateContext<IProductApiStatusState>, action: VersionsError)
    {
        patchState({ versionsError: action.error })
    }

    @Action(CreateVersionError)
    public createVersionError({ patchState }: StateContext<IProductApiStatusState>, action: CreateVersionError)
    {
        patchState({ createVersionError: action.error })
    }

    @Action(ClearCreateVersionError)
    public clearCreateVersionError({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ createVersionError: null })
    }

    @Action(ImportedTranslation)
    public importVersionError({ patchState }: StateContext<IProductApiStatusState>, action: ImportedTranslation)
    {
        patchState({ importedTranslation: action.value })
    }

    @Action(ClearImportedTranslation)
    public clearImportVersionError({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ importedTranslation: null })
    }

    @Action(CreateProductError)
    public createProductError({ patchState }: StateContext<IProductApiStatusState>, action: CreateProductError)
    {
        patchState({ createProductError: action.error })
    }

    @Action(ClearCreateProductError)
    public clearCreateProductError({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ createProductError: null })
    }

    @Action(CreateNoteError)
    public createNoteError({ patchState }: StateContext<IProductApiStatusState>, action: CreateNoteError)
    {
        patchState({ createNoteError: action.error })
    }

    @Action(ClearCreateNoteError)
    public clearCreateNoteError({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ createNoteError: null })
    }


    @Action(CreateSubscriptionError)
    public createSubscriptionError({ patchState }: StateContext<IProductApiStatusState>, action: CreateSubscriptionError)
    {
        patchState({ createSubscriptionError: action.error })
    }

    @Action(CreateUnsubscribeError)
    public createUnsubscribeError({ patchState }: StateContext<IProductApiStatusState>, action: CreateUnsubscribeError)
    {
        patchState({ createUnsubscribeError: action.error })
    }

    @Action(ClearCreateSubscriptionError)
    public clearCreateSubscriptionError({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ createSubscriptionError: null })
    }

    @Action(ClearCreateUnsubscribeError)
    public clearCreateUnsubscribeError({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ createUnsubscribeError: null })
    }

    @Action(ClearEdited)
    public clearEdited({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ editedValue: null })
    }

    @Action(ClearExported)
    public clearExported({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ exportedTranslation: null })
    }

    @Action(ClearCreatedVersion)
    public clearCreatedVersion({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ addedVersion: null })
    }

    @Action(ClearCreatedProduct)
    public clearCreatedProduct({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ addedProduct: null })
    }

    @Action(ClearCreatedNote)
    public clearCreatedNote({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ addedNote: null })
    }

    @Action(ClearCreatedSubscription)
    public clearCreatedSubscription({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ addedSubscription: null })
    }

    @Action(ClearUnsubscribed)
    public clearUnsubscribed({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ unsubscribed: null })
    }

    @Action(ClearRemovedSubscription)
    public clearRemovedSubscription({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ removedSubscription: null })
    }

    @Action(ClearCheckVersion)
    public clearCheckVersion({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ checkVersion: null })
    }

    @Action(ClearCheckNote)
    public clearCheckNote({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ checkNote: null })
    }

    @Action(ClearVersionsError)
    public clearVersionError({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ versionsError: null })
    }

    @Action(ClearNotesError)
    public clearNoteError({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ notesError: null })
    }

    @Action(NotesError)
    public voteError({ patchState }: StateContext<IProductApiStatusState>, action: NotesError)
    {
        patchState({ notesError: action.error })
    }

    @Action(InitialVersionsLoaded)
    initialVersionsLoaded({ patchState }: StateContext<IProductApiStatusState>, action: InitialVersionsLoaded)
    {
        patchState({ initialVersionLoad: action.value });
    }

    @Action(ProductDeleted)
    productDeleted({ patchState }: StateContext<IProductApiStatusState>, action: ProductDeleted)
    {
        patchState({ productDeleted: action.value });
    }

    @Action(ClearProductDeleted)
    clearProductDeleted({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ productDeleted: null });
    }

    @Action(VersionDeleted)
    versionDeleted({ patchState }: StateContext<IProductApiStatusState>, action: VersionDeleted)
    {
        patchState({ versionDeleted: action.value });
    }

    @Action(ClearVersionDeleted)
    clearVersionDeleted({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ versionDeleted: null });
    }

    @Action(NoteDeleted)
    noteDeleted({ patchState }: StateContext<IProductApiStatusState>, action: NoteDeleted)
    {
        patchState({ noteDeleted: action.value });
    }

    @Action(ClearNoteDeleted)
    clearNoteDeleted({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ noteDeleted: null });
    }

    @Action(CheckedVersionAvailable)
    public CheckedVersionAvailable({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ checkVersion: CheckStatusEnum.Available })
    }

    @Action(CheckedNoteAvailable)
    public checkedNoteTrue({ patchState }: StateContext<IProductApiStatusState>)
    {
        patchState({ checkNote: CheckStatusEnum.Available })
    }
}
